import type { PriceHistoryRow } from "../../api/articles";
import { formatDateShort } from "../../lib/format";

import { rollingMonthCutoffIso } from "./savings";

export type ArticleDetailPeriodMode =
  | "rolling36m"
  | "rolling12m"
  | "lastQuarter"
  | "rolling3m"
  | "fiscalYear"
  | "custom";

export type ArticleDetailPeriodSelection = {
  mode: ArticleDetailPeriodMode;
  customFrom: string;
  customTo: string;
};

export type ArticleDetailPeriod = {
  mode: ArticleDetailPeriodMode;
  from: string | null;
  to: string | null;
  label: string;
  compactLabel: string;
  rangeLabel: string;
  fileSlug: string;
  isValid: boolean;
  error: string | null;
};

export type ArticleDateRange = Pick<
  ArticleDetailPeriod,
  "from" | "to" | "isValid"
>;

export const DEFAULT_ARTICLE_DETAIL_PERIOD: ArticleDetailPeriodSelection = {
  mode: "rolling36m",
  customFrom: "",
  customTo: "",
};

export const ARTICLE_DETAIL_PERIOD_OPTIONS: readonly {
  value: ArticleDetailPeriodMode;
  label: string;
}[] = [
  { value: "rolling36m", label: "3 Jahre" },
  { value: "rolling12m", label: "12 Monate" },
  { value: "lastQuarter", label: "Letztes Quartal" },
  { value: "rolling3m", label: "3 Monate" },
  { value: "fiscalYear", label: "Geschäftsjahr" },
  { value: "custom", label: "Benutzerdefiniert" },
];

const MONTH_LABELS = [
  "Jän",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
] as const;

export function resolveArticleDetailPeriod({
  selection,
  anchorDate,
  fiscalYearStartMonth,
}: {
  selection: ArticleDetailPeriodSelection;
  anchorDate: string | null;
  fiscalYearStartMonth: number;
}): ArticleDetailPeriod {
  const anchor = normalizeIsoDate(anchorDate);
  if (!anchor) {
    return invalidPeriod(
      selection.mode,
      "Zeitraum ohne belegtes Stichtagsdatum nicht verfügbar.",
    );
  }

  switch (selection.mode) {
    case "rolling36m":
      return validPeriod({ mode: selection.mode, from: rollingMonthCutoffIso(anchor, 36), to: anchor, label: "Letzte 3 Jahre", compactLabel: "3 Jahre", fileSlug: "36m" });
    case "rolling12m":
      return validPeriod({
        mode: selection.mode,
        from: rollingMonthCutoffIso(anchor, 12),
        to: anchor,
        label: "Letzte 12 Monate",
        compactLabel: "12 M.",
        fileSlug: "12m",
      });
    case "rolling3m":
      return validPeriod({
        mode: selection.mode,
        from: rollingMonthCutoffIso(anchor, 3),
        to: anchor,
        label: "Letzte 3 Monate",
        compactLabel: "3 M.",
        fileSlug: "3m",
      });
    case "lastQuarter": {
      const [year, month] = isoParts(anchor);
      const currentQuarterStart = Math.floor((month - 1) / 3) * 3 + 1;
      const previousQuarterEndMonth =
        currentQuarterStart === 1 ? 12 : currentQuarterStart - 1;
      const previousQuarterYear = currentQuarterStart === 1 ? year - 1 : year;
      const previousQuarterStartMonth = previousQuarterEndMonth - 2;
      const quarter = Math.floor((previousQuarterStartMonth - 1) / 3) + 1;
      return validPeriod({
        mode: selection.mode,
        from: isoDate(previousQuarterYear, previousQuarterStartMonth, 1),
        to: isoDate(
          previousQuarterYear,
          previousQuarterEndMonth,
          daysInMonth(previousQuarterYear, previousQuarterEndMonth),
        ),
        label: `Letztes volles Quartal · Q${quarter} ${previousQuarterYear}`,
        compactLabel: `Q${quarter} ${previousQuarterYear}`,
        fileSlug: `q${quarter}-${previousQuarterYear}`,
      });
    }
    case "fiscalYear": {
      if (
        !Number.isInteger(fiscalYearStartMonth) ||
        fiscalYearStartMonth < 1 ||
        fiscalYearStartMonth > 12
      ) {
        return invalidPeriod(
          selection.mode,
          "Geschäftsjahresbeginn ist nicht verfügbar.",
        );
      }
      const [year, month] = isoParts(anchor);
      const startYear = month >= fiscalYearStartMonth ? year : year - 1;
      const endMonth =
        fiscalYearStartMonth === 1 ? 12 : fiscalYearStartMonth - 1;
      const endYear = fiscalYearStartMonth === 1 ? startYear : startYear + 1;
      const compactLabel =
        fiscalYearStartMonth === 1
          ? `GJ ${startYear}`
          : `GJ ${startYear}/${String(endYear).slice(-2)}`;
      const monthRange = `${MONTH_LABELS[fiscalYearStartMonth - 1]}–${MONTH_LABELS[endMonth - 1]}`;
      return validPeriod({
        mode: selection.mode,
        from: isoDate(startYear, fiscalYearStartMonth, 1),
        to: anchor,
        label: `${compactLabel} (${monthRange})`,
        compactLabel,
        fileSlug:
          fiscalYearStartMonth === 1
            ? `gj-${startYear}`
            : `gj-${startYear}-${String(endYear).slice(-2)}`,
      });
    }
    case "custom": {
      const from = normalizeIsoDate(selection.customFrom);
      const to = normalizeIsoDate(selection.customTo);
      if (!from || !to) {
        return invalidPeriod(
          selection.mode,
          "Bitte ein gültiges Von- und Bis-Datum wählen.",
        );
      }
      if (from > to) {
        return invalidPeriod(
          selection.mode,
          "Das Von-Datum muss vor dem Bis-Datum liegen.",
        );
      }
      if (to > anchor) {
        return invalidPeriod(
          selection.mode,
          "Das Bis-Datum darf nicht nach dem Datenstichtag liegen.",
        );
      }
      return validPeriod({
        mode: selection.mode,
        from,
        to,
        label: `Benutzerdefiniert · ${formatDateShort(from)}–${formatDateShort(to)}`,
        compactLabel: "Eigener Zeitraum",
        fileSlug: `${from}_${to}`,
      });
    }
  }
}

export function filterArticleRowsByRange<T extends Pick<PriceHistoryRow, "doc_date">>(
  rows: readonly T[],
  range: ArticleDateRange,
): T[] {
  if (!range.isValid || !range.from || !range.to) return [];
  const { from, to } = range;
  return rows.filter((row) => {
    const date = normalizeIsoDate(row.doc_date);
    return date !== null && date >= from && date <= to;
  });
}

export function normalizeIsoDate(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const iso = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [year, month, day] = isoParts(iso);
  if (
    year < 1 ||
    year > 9999 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    return null;
  }
  return iso;
}

function validPeriod({
  mode,
  from,
  to,
  label,
  compactLabel,
  fileSlug,
}: {
  mode: ArticleDetailPeriodMode;
  from: string | null;
  to: string;
  label: string;
  compactLabel: string;
  fileSlug: string;
}): ArticleDetailPeriod {
  if (!from) return invalidPeriod(mode, "Zeitraum konnte nicht berechnet werden.");
  return {
    mode,
    from,
    to,
    label,
    compactLabel,
    rangeLabel: `${formatDateShort(from)}–${formatDateShort(to)}`,
    fileSlug,
    isValid: true,
    error: null,
  };
}

function invalidPeriod(
  mode: ArticleDetailPeriodMode,
  error: string,
): ArticleDetailPeriod {
  return {
    mode,
    from: null,
    to: null,
    label: mode === "custom" ? "Benutzerdefiniert" : "Zeitraum nicht verfügbar",
    compactLabel: "Zeitraum",
    rangeLabel: "–",
    fileSlug: "ungueltig",
    isValid: false,
    error,
  };
}

function isoParts(iso: string): [number, number, number] {
  const [year, month, day] = iso.split("-", 3).map(Number);
  return [year, month, day];
}

function isoDate(year: number, month: number, day: number): string {
  return [
    String(year).padStart(4, "0"),
    String(month).padStart(2, "0"),
    String(day).padStart(2, "0"),
  ].join("-");
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
